import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { User, Camera, Lock, CreditCard, CheckCircle, AlertTriangle, Loader2, Upload, Star, Award, RefreshCw, Tent, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import IdentityVerificationCard from "@/components/IdentityVerificationCard";
import StripeConnectCard from "@/components/StripeConnectCard";
import { Switch } from "@/components/ui/switch";
import { useSiteSettings } from "@/contexts/SettingsContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatCad = (n) => {
  const v = typeof n === "number" ? n : parseFloat(n);
  if (!isFinite(v)) return "10,00";
  return v.toFixed(2).replace(".", ",");
};
const formatFeePct = (rate) => {
  const v = typeof rate === "number" ? rate : parseFloat(rate);
  if (!isFinite(v)) return "3";
  // Trim trailing zero on whole numbers (3.0 -> 3, 2.5 -> 2,5)
  const pct = v * 100;
  return (Number.isInteger(pct) ? String(pct) : pct.toFixed(2).replace(/\.?0+$/, "")).replace(".", ",");
};

export default function ProfilePage() {
  const { user } = useAuth();
  const { settings } = useSiteSettings();
  const appName = settings?.app_name || "CampSpot";
  const navigate = useNavigate();
  const feePctLabel = formatFeePct(settings?.platform_fee_rate);
  const hostShareLabel = formatFeePct(1 - (typeof settings?.platform_fee_rate === "number" ? settings.platform_fee_rate : 0.03));
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [changingPwd, setChangingPwd] = useState(false);
  const fileRef = useRef(null);

  const [profile, setProfile] = useState({ name: "", phone: "", bio: "", profile_photo: "" });
  const [passwords, setPasswords] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [subscription, setSubscription] = useState({ status: "inactive", expires: "", price: 10, auto_renew: true, grace_until: null, has_saved_card: false });
  const [subscribing, setSubscribing] = useState(false);
  const [togglingAutoRenew, setTogglingAutoRenew] = useState(false);
  const [ratingSummary, setRatingSummary] = useState({ avg_rating: 0, rating_count: 0, ratings: [], is_superstar: false });
  const [verification, setVerification] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/auth/me").then(r => {
        const u = r.data;
        setProfile({ name: u.name || "", phone: u.phone || "", bio: u.bio || "", profile_photo: u.profile_photo || "" });
        setVerification(u.identity_verification || { status: "unverified" });
        if (u.id) {
          api.get(`/users/${u.id}/ratings`).then(rr => setRatingSummary(rr.data)).catch(() => {});
        }
      }),
      api.get("/subscription/status").then(r => setSubscription(r.data)),
    ]).catch(() => {}).finally(() => setLoading(false));

    // Verify subscription if returning from Stripe
    const sessionId = searchParams.get("session_id");
    const subSuccess = searchParams.get("subscription");
    if (subSuccess === "success" && sessionId) {
      api.get(`/subscription/verify/${sessionId}`).then(r => {
        if (r.data.status === "active") {
          setSubscription({ status: "active", expires: r.data.expires, price: 10 });
          toast.success("Abonnement active avec succes!");
        }
      }).catch(() => {});
    }
  }, [searchParams]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5 MB"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setProfile(p => ({ ...p, profile_photo: data.url }));
      await api.put("/auth/profile", { profile_photo: data.url });
      toast.success("Photo de profil mise a jour!");
    } catch { toast.error("Erreur upload"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const getPhotoUrl = () => {
    if (!profile.profile_photo) return null;
    if (profile.profile_photo.startsWith("/api/")) return `${BACKEND_URL}${profile.profile_photo}`;
    return profile.profile_photo;
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await api.put("/auth/profile", { name: profile.name, phone: profile.phone, bio: profile.bio });
      toast.success("Profil mis a jour!");
    } catch { toast.error("Erreur"); }
    finally { setSaving(false); }
  };

  const handleChangePassword = async () => {
    if (passwords.new_password !== passwords.confirm_password) { toast.error("Les mots de passe ne correspondent pas"); return; }
    if (passwords.new_password.length < 6) { toast.error("Minimum 6 caractères"); return; }
    setChangingPwd(true);
    try {
      await api.put("/auth/change-password", { current_password: passwords.current_password, new_password: passwords.new_password });
      toast.success("Mot de passe modifié!");
      setPasswords({ current_password: "", new_password: "", confirm_password: "" });
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setChangingPwd(false); }
  };

  const handleSubscribe = async () => {
    setSubscribing(true);
    try {
      const { data } = await api.post("/subscription/checkout", { origin_url: window.location.origin });
      if (data.free_mode) {
        toast.success(data.message || "Abonnement active!");
        const r = await api.get("/subscription/status");
        setSubscription(r.data);
      } else if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSubscribing(false); }
  };

  const handleToggleAutoRenew = async (enabled) => {
    setTogglingAutoRenew(true);
    // Optimistic UI
    setSubscription(s => ({ ...s, auto_renew: enabled }));
    try {
      await api.put("/subscription/auto-renew", { enabled });
      toast.success(enabled ? "Renouvellement automatique activé" : "Renouvellement automatique désactivé");
    } catch (err) {
      // Revert
      setSubscription(s => ({ ...s, auto_renew: !enabled }));
      toast.error(err.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setTogglingAutoRenew(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-4 border-zinc-300 border-t-emerald-700 rounded-full" /></div>;

  const isActive = subscription.status === "active";
  const photoUrl = getPhotoUrl();
  const isHost = user?.role === "admin" || (user?.spaces_count ?? 0) > 0 || isActive || subscription.status === "grace";

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 animate-fade-in">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950 mb-6" data-testid="profile-title">Mon profil</h1>

      <div className="space-y-6">
        {/* Profile Photo & Info */}
        <Card className="border border-zinc-200 rounded-2xl" data-testid="profile-info-card">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Informations personnelles</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Photo */}
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="h-20 w-20 rounded-full bg-emerald-100 flex items-center justify-center overflow-hidden border-2 border-zinc-200">
                  {photoUrl ? (
                    <img src={photoUrl} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <User className="h-8 w-8 text-emerald-700" />
                  )}
                </div>
                <label className="absolute -bottom-1 -right-1 h-8 w-8 bg-emerald-600 rounded-full flex items-center justify-center cursor-pointer hover:bg-emerald-700 transition-colors shadow-md" data-testid="photo-upload-label">
                  {uploading ? <Loader2 className="h-4 w-4 text-white animate-spin" /> : <Camera className="h-4 w-4 text-white" />}
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} disabled={uploading} data-testid="profile-photo-input" />
                </label>
              </div>
              <div>
                <p className="font-heading font-bold text-zinc-950">{profile.name || user?.name}</p>
                <p className="text-sm text-zinc-500">{user?.email}</p>
                {ratingSummary.rating_count > 0 ? (
                  <div className="flex items-center gap-2 mt-1 flex-wrap" data-testid="profile-rating-badge">
                    <div className="flex items-center gap-1">
                      <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                      <span className="text-sm font-semibold text-zinc-900">{ratingSummary.avg_rating}/5</span>
                      <span className="text-xs text-zinc-400">({ratingSummary.rating_count} avis)</span>
                    </div>
                    {ratingSummary.is_superstar && (
                      <Badge className="bg-gradient-to-r from-amber-400 to-amber-500 text-white border-0 gap-1 shadow-sm" data-testid="superstar-badge">
                        <Award className="h-3 w-3" /> Hôte Superstar
                      </Badge>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-zinc-400 mt-1" data-testid="profile-rating-empty">Pas encore d'avis</p>
                )}
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Nom complet</Label>
                <Input value={profile.name} onChange={e => setProfile(p => ({ ...p, name: e.target.value }))} className="mt-1 rounded-xl" data-testid="profile-name-input" />
              </div>
              <div>
                <Label>Telephone</Label>
                <Input value={profile.phone} onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))} placeholder="514-555-1234" className="mt-1 rounded-xl" data-testid="profile-phone-input" />
              </div>
            </div>
            <div>
              <Label>Bio</Label>
              <Textarea value={profile.bio} onChange={e => setProfile(p => ({ ...p, bio: e.target.value }))} placeholder="Parlez de vous..." className="mt-1 rounded-xl min-h-[80px]" data-testid="profile-bio-input" />
            </div>
            <Button onClick={handleSaveProfile} disabled={saving} className="bg-emerald-700 text-white hover:bg-emerald-800 rounded-xl" data-testid="save-profile-btn">
              {saving ? "Sauvegarde..." : "Sauvegarder le profil"}
            </Button>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card className="border border-zinc-200 rounded-2xl" data-testid="password-card">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2"><Lock className="h-5 w-5" /> Changer le mot de passe</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Mot de passe actuel</Label>
              <Input type="password" value={passwords.current_password} onChange={e => setPasswords(p => ({ ...p, current_password: e.target.value }))} className="mt-1 rounded-xl" data-testid="current-password-input" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Nouveau mot de passe</Label>
                <Input type="password" value={passwords.new_password} onChange={e => setPasswords(p => ({ ...p, new_password: e.target.value }))} placeholder="Min 6 caractères" className="mt-1 rounded-xl" data-testid="new-password-input" />
              </div>
              <div>
                <Label>Confirmer</Label>
                <Input type="password" value={passwords.confirm_password} onChange={e => setPasswords(p => ({ ...p, confirm_password: e.target.value }))} className="mt-1 rounded-xl" data-testid="confirm-password-input" />
              </div>
            </div>
            <Button onClick={handleChangePassword} disabled={changingPwd || !passwords.current_password || !passwords.new_password} variant="outline" className="rounded-xl" data-testid="change-password-btn">
              <Lock className="h-4 w-4 mr-2" /> {changingPwd ? "Modification..." : "Modifier le mot de passe"}
            </Button>
          </CardContent>
        </Card>

        {/* Identity Verification (driver's license) */}
        <IdentityVerificationCard verification={verification} onUpdated={setVerification} />

        {isHost ? (
          <>
        {/* Stripe Connect (host payouts) */}
        <StripeConnectCard />

        {/* Subscription */}
        <Card className={`border rounded-2xl ${isActive ? "border-emerald-200 bg-emerald-50/30" : "border-zinc-200"}`} data-testid="subscription-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg flex items-center gap-2"><CreditCard className="h-5 w-5" /> Abonnement propriétaire</CardTitle>
              <Badge className={`${isActive ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-500"} border-0`} data-testid="subscription-badge">
                {isActive ? "Actif" : "Inactif"}
              </Badge>
            </div>
            <CardDescription>
              L'abonnement est requis pour publier des terrains sur {appName}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isActive ? (
              <div className="space-y-3" data-testid="subscription-active">
                <div className="flex items-center gap-2 text-emerald-700">
                  <CheckCircle className="h-5 w-5" />
                  <span className="font-medium">Abonnement actif</span>
                </div>
                <p className="text-sm text-zinc-500">
                  Expiré le : <strong>{subscription.expires ? new Date(subscription.expires).toLocaleDateString("fr-CA") : "N/A"}</strong>
                </p>
                <p className="text-sm text-zinc-500">Vous pouvez publier des terrains et recevoir des réservations.</p>

                {/* Auto-renewal toggle */}
                <div className="p-4 rounded-xl bg-white border border-zinc-200" data-testid="auto-renew-section">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <RefreshCw className="h-4 w-4 text-emerald-700" />
                        <span className="font-medium text-zinc-900 text-sm">Renouvellement automatique</span>
                      </div>
                      <p className="text-xs text-zinc-500">
                        {subscription.auto_renew
                          ? `Votre carte sera débitée automatiquement de ${formatCad(subscription.price)} $ à l'expiration.`
                          : "Vous devrez renouveler manuellement avant la date d'expiration pour garder vos terrains visibles."}
                      </p>
                    </div>
                    <Switch
                      checked={!!subscription.auto_renew}
                      onCheckedChange={handleToggleAutoRenew}
                      disabled={togglingAutoRenew}
                      data-testid="auto-renew-toggle"
                    />
                  </div>
                  {subscription.auto_renew && !subscription.has_saved_card && (
                    <div className="mt-2 p-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-700 flex items-start gap-1.5">
                      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <span>Aucune carte enregistrée n'est encore liée. Le renouvellement automatique commencera après votre prochain paiement.</span>
                    </div>
                  )}
                </div>

                <div className="p-3 rounded-xl bg-zinc-100 text-sm text-zinc-600">
                  <strong>Frais transactionnels :</strong> {feePctLabel}% sont prélevés sur chaque réservation. Vous recevez {hostShareLabel}% du montant.
                </div>
              </div>
            ) : subscription.status === "grace" ? (
              <div className="space-y-3" data-testid="subscription-grace">
                <div className="flex items-center gap-2 text-amber-600">
                  <AlertTriangle className="h-5 w-5" />
                  <span className="font-medium">Période de grâce</span>
                </div>
                <p className="text-sm text-zinc-600">
                  Votre abonnement a expiré mais vos terrains restent visibles jusqu'au <strong>{subscription.grace_until ? new Date(subscription.grace_until).toLocaleDateString("fr-CA") : "N/A"}</strong>.
                </p>
                <p className="text-sm text-zinc-500">Renouvelez maintenant pour éviter que vos terrains soient masqués.</p>
                <Button onClick={handleSubscribe} disabled={subscribing} className="w-full bg-amber-600 hover:bg-amber-700 text-white rounded-xl h-11" data-testid="renew-btn">
                  <CreditCard className="h-4 w-4 mr-2" /> {subscribing ? "Redirection..." : `Renouveler - ${formatCad(subscription.price)} $/an`}
                </Button>
              </div>
            ) : (
              <div className="space-y-4" data-testid="subscription-inactive">
                <div className="flex items-center gap-2 text-amber-600">
                  <AlertTriangle className="h-5 w-5" />
                  <span className="font-medium">Abonnement requis pour publier</span>
                </div>
                <div className="p-4 rounded-xl bg-white border border-zinc-200">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="font-heading font-bold text-xl text-zinc-950">
                        {subscription.price > 0 ? `${formatCad(subscription.price)} $ / an` : "Gratuit"}
                      </p>
                      <p className="text-sm text-zinc-500">Publiez vos terrains et accueillez des campeurs</p>
                    </div>
                  </div>
                  <ul className="text-sm text-zinc-600 space-y-1.5 mb-4">
                    <li className="flex items-center gap-2"><CheckCircle className="h-3.5 w-3.5 text-emerald-600" /> Publication illimitée de terrains</li>
                    <li className="flex items-center gap-2"><CheckCircle className="h-3.5 w-3.5 text-emerald-600" /> Messagerie avec les campeurs</li>
                    <li className="flex items-center gap-2"><CheckCircle className="h-3.5 w-3.5 text-emerald-600" /> Tableau de bord et statistiques</li>
                    <li className="flex items-center gap-2"><CheckCircle className="h-3.5 w-3.5 text-emerald-600" /> Calendrier de gestion des disponibilités</li>
                    <li className="text-xs text-zinc-400 mt-2">Frais transactionnels de {feePctLabel}% par réservation</li>
                  </ul>
                  <Button onClick={handleSubscribe} disabled={subscribing} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl h-11" data-testid="subscribe-btn">
                    <CreditCard className="h-4 w-4 mr-2" />
                    {subscribing
                      ? (subscription.price > 0 ? "Redirection..." : "Activation...")
                      : (subscription.price > 0 ? `S'abonner - ${formatCad(subscription.price)} $/an` : "Activer mon abonnement gratuit")}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
          </>
        ) : (
          /* Become a Host CTA (non-hosts) */
          <Card className="border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white rounded-2xl overflow-hidden" data-testid="become-host-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Tent className="h-5 w-5 text-emerald-700" /> Devenir hôte
              </CardTitle>
              <CardDescription>
                Publiez votre terrain et accueillez des campeurs sur {appName}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-zinc-600">
                Suivez les 3 étapes pour publier votre terrain :
              </p>
              <ol className="space-y-2.5 text-sm">
                <li className="flex items-start gap-3" data-testid="become-host-step-1">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 font-bold text-xs">1</span>
                  <div>
                    <p className="font-medium text-zinc-900">Activer l'abonnement</p>
                    <p className="text-xs text-zinc-500">Requis pour publier vos terrains</p>
                  </div>
                </li>
                <li className="flex items-start gap-3" data-testid="become-host-step-2">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 font-bold text-xs">2</span>
                  <div>
                    <p className="font-medium text-zinc-900">Vérifier votre identité</p>
                    <p className="text-xs text-zinc-500">Permis de conduire (recto/verso + selfie)</p>
                  </div>
                </li>
                <li className="flex items-start gap-3" data-testid="become-host-step-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 font-bold text-xs">3</span>
                  <div>
                    <p className="font-medium text-zinc-900">Configurer les paiements</p>
                    <p className="text-xs text-zinc-500">Compte Stripe Connect pour recevoir vos revenus</p>
                  </div>
                </li>
              </ol>
              <Button
                onClick={() => navigate("/host-onboarding")}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl h-11"
                data-testid="become-host-btn"
              >
                Publier un terrain <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}
        <Card className="border border-zinc-200 rounded-2xl" data-testid="reviews-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg flex items-center gap-2"><Star className="h-5 w-5 fill-amber-400 text-amber-400" /> Avis reçus</CardTitle>
              {ratingSummary.rating_count > 0 && (
                <div className="flex items-center gap-1 text-sm font-semibold text-zinc-900">
                  <span>{ratingSummary.avg_rating}/5</span>
                  <span className="text-xs text-zinc-400 font-normal">({ratingSummary.rating_count})</span>
                </div>
              )}
            </div>
            <CardDescription>Les évaluations laissees par les campeurs et propriétaires</CardDescription>
          </CardHeader>
          <CardContent>
            {ratingSummary.ratings.length === 0 ? (
              <div className="text-center py-6 text-sm text-zinc-400" data-testid="no-reviews">
                <Star className="h-8 w-8 mx-auto mb-2 text-zinc-200" />
                Aucun avis pour le moment. Les avis apparaissent après un séjour complété.
              </div>
            ) : (
              <div className="space-y-3">
                {ratingSummary.ratings.map((r, i) => (
                  <div key={r.id || i} className="p-4 rounded-xl bg-zinc-50 border border-zinc-100" data-testid={`review-${i}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="h-9 w-9 rounded-full bg-emerald-100 flex items-center justify-center text-sm font-bold text-emerald-700">
                          {r.rater_name?.charAt(0)?.toUpperCase() || "?"}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-zinc-900">{r.rater_name}</p>
                          <p className="text-xs text-zinc-400">{r.rater_role === "host" ? "Propriétaire" : "Campeur"}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-0.5">
                        {[1,2,3,4,5].map(s => (
                          <Star key={s} className={`h-3.5 w-3.5 ${s <= r.rating ? "fill-amber-400 text-amber-400" : "text-zinc-300"}`} />
                        ))}
                      </div>
                    </div>
                    {r.comment && <p className="text-sm text-zinc-600 italic">"{r.comment}"</p>}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
